import { LogoutOptions, PopupConfigOptions, PopupLoginOptions, useAuth0 } from "@auth0/auth0-react";
import { AuthApi, EventsApi, FreeplayUser, GamesApi, UsersApi } from "@freeplay/shared-code";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { getEnv } from "../utils/getEnv";
import { AuthAPIWrapper } from "./wrappers/AuthAPIWrapper";
import { EventsAPIWrapper } from "./wrappers/EventsAPIWrapper";
import { GamesAPIWrapper } from "./wrappers/GamesAPIWrapper";
import { UserAPIWrapper } from "./wrappers/UserAPIWrapper";

export type ServiceContextProps = {
    fpUser?: FreeplayUser;
    initialized: boolean;
    gamesApi: GamesAPIWrapper;
    eventsApi: EventsAPIWrapper;
    authApi: AuthAPIWrapper;
    userApi: UserAPIWrapper;
    login: (options?: PopupLoginOptions, config?: PopupConfigOptions) => Promise<void>;
    logout: (options?: LogoutOptions) => void;
    activeToken: string;
    edgeDeploy: boolean;
    alternateTheme: boolean;
};

export const ServiceContext = React.createContext<ServiceContextProps>({
    gamesApi: new GamesAPIWrapper(),
    eventsApi: new EventsAPIWrapper(),
    authApi: new AuthAPIWrapper(),
    userApi: new UserAPIWrapper(),
    login: async (options, config) => {
        /* no-op */
    },
    logout: (options) => {
        /* no-op */
    },
    initialized: false,
    activeToken: "",
    edgeDeploy: false,
    alternateTheme: false,
});

type ServiceProviderProps = {
    children?: React.ReactNode;
    deployType?: string;
    tenantSlug: string;
    alternateTheme?: boolean;
};

export const ServiceProvider = ({
    children,
    deployType,
    tenantSlug,
    alternateTheme,
}: ServiceProviderProps): JSX.Element => {
    const { user, logout, loginWithPopup, isLoading, getAccessTokenSilently } = useAuth0();

    const [serviceContextState, setServiceContextState] = useState<ServiceContextProps>({
        gamesApi: new GamesAPIWrapper(),
        eventsApi: new EventsAPIWrapper(),
        userApi: new UserAPIWrapper(),
        authApi: new AuthAPIWrapper(),
        login: async (options, config) => {
            /* no-op */
        },
        logout: (options?: LogoutOptions | undefined) => {
            /* no-op */
        },
        initialized: false,
        activeToken: "",
        edgeDeploy: deployType === "edge",
        alternateTheme: alternateTheme ?? false,
    });

    const { gamesApi, eventsApi, authApi, userApi, initialized, activeToken, login } = serviceContextState;

    const loginAndInit = useCallback(
        async (options, config) => {
            setServiceContextState({
                ...serviceContextState,
                initialized: false,
            });
            await loginWithPopup(options, config);
        },
        [serviceContextState, loginWithPopup],
    );

    const logoutFromiDP = (options?: LogoutOptions | undefined) => {
        // create logout sequence + update it.
        // logout only local, will avoid redirects and dropping back to main root
        logout({ localOnly: true });
        // clear the state for this user, reset the login function to work properly
        // Todo - it appears the app still holds some state. so, if i play a game logged in and submit picks,
        gamesApi.clearUser();
        eventsApi.clearUser();
        userApi.clearUser();
        authApi.clearUser();
        // Todo - then logout, and then goto that game (in the same state), it still shows...
        setServiceContextState((previousState) => {
            return {
                ...previousState,
                activeToken: "",
                fpUser: undefined,
                initialized: false,
                logout: (options?: LogoutOptions | undefined) => {
                    /* no-op */
                },
                login: loginAndInit,
            };
        });
    };

    // API Initilization
    useEffect(() => {
        if (!isLoading) {
            (async () => {
                const token = user ? await getAccessTokenSilently() : "";
                if (!initialized || token !== activeToken) {
                    initializeApis(gamesApi, eventsApi, authApi, userApi, token, tenantSlug);
                    let tempFpUser: FreeplayUser | undefined = undefined;
                    // If we already have a user and are logged in, don't even provide
                    // the login method to the app
                    let updatedLogin = login;
                    if (token === "") {
                        updatedLogin = loginAndInit;
                    } else {
                        tempFpUser = (await authApi.loginUser()) as FreeplayUser;
                    }
                    // need a way to reset fpuser?
                    setServiceContextState({
                        ...serviceContextState,
                        initialized: true,
                        activeToken: token,
                        fpUser: tempFpUser,
                        login: updatedLogin,
                        // enable logout
                        logout: logoutFromiDP,
                    });
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    return <ServiceContext.Provider value={serviceContextState}>{children}</ServiceContext.Provider>;
};

export const useServices = (): ServiceContextProps => useContext(ServiceContext) as ServiceContextProps;

const initializeApis = (
    gamesApi: GamesAPIWrapper,
    eventsApi: EventsAPIWrapper,
    authApi: AuthAPIWrapper,
    userApi: UserAPIWrapper,
    token: string,
    tenantSlug: string,
) => {
    const gamesApiConnection = new GamesApi({
        basePath: getEnv().REACT_APP_API_URL,
    });

    const eventsApiConnection = new EventsApi({
        basePath: getEnv().REACT_APP_API_URL,
    });

    const authApiConnection = new AuthApi({
        basePath: getEnv().REACT_APP_API_URL,
    });

    const userApiConnection = new UsersApi({
        basePath: getEnv().REACT_APP_API_URL,
    });

    gamesApi.init(gamesApiConnection, token, tenantSlug);
    eventsApi.init(eventsApiConnection, token, tenantSlug);
    authApi.init(authApiConnection, token, tenantSlug);
    userApi.init(userApiConnection, token, tenantSlug);
};
